<script async setup lang="ts">
import CloseSvg from '@/components/svgs/CloseSvg.vue'
import { CULTURE } from '@/composables/helpers'
import type { IAppliedFilter, IAppliedFilters, IFilterStoreInterface } from '@/composables/interfaces/appInterfaces'
import t from '@/i18n/translations'
import { filterStore, filtersData, ratingsFilterSelected } from '@/store/filter.store'
import { onMounted, ref, watch } from 'vue'

const appliedFilters = ref<IAppliedFilters>({ appliedFilter: [] })

const emit = defineEmits(['ratings-selected'])

function addAppliedFilters(filterStore: IFilterStoreInterface): void {
  const results: IAppliedFilter[] = []
  if (filterStore.accotypeIds.length) {
    const accoTypes = filtersData.value!.accotypes.filter((item) => filterStore.accotypeIds.includes(item.id))
    results.push(...accoTypes.map((item) => ({ id: item.id, name: item.name })))
  }

  if (filterStore.ratingFrom && filterStore.ratingTo) {
    const ratingLabel =
      filterStore.ratingFrom === filterStore.ratingTo
        ? `${filterStore.ratingFrom} ${t(`${CULTURE}.filter.appliedFilters.stars`)}`
        : `${filterStore.ratingFrom} - ${filterStore.ratingTo} ${t(`${CULTURE}.filter.appliedFilters.stars`)}`
    results.push({ id: filterStore.ratingFrom, name: ratingLabel })
  }

  if (filterStore.boardingIds.length) {
    const boarding = filtersData.value!.boardings.find((b) => b.id === filterStore.boardingIds[0]) // radiobutton so only one value in array
    if (boarding != null) {
      results.push({
        id: boarding.id,
        name: boarding.name
      })
    }
  }

  if (filterStore.amenityIds.length) {
    const amenities = filtersData.value!.amenities.filter((item) => filterStore.amenityIds.includes(item.id))
    results.push(...amenities.map((item) => ({ id: item.id, name: item.name })))
  }

  if (filterStore.themeId) {
    const theme = filtersData.value!.themes.find((item) => filterStore.themeId === item.id)
    if (theme) results.push({ id: theme.id, name: theme.name })
  }

  appliedFilters.value.appliedFilter = results
}

function removeAppliedFilters(filterIds: string | number | number[]): void {
  filterStore.value.accotypeIds = filterStore.value.accotypeIds.filter((id) => filterIds !== id)

  if (ratingsFilterSelected.value.includes(filterIds as number)) {
    filterStore.value.ratingFrom = 0
    filterStore.value.ratingTo = 5.5
    ratingsFilterSelected.value = []
  }

  if (filterStore.value.boardingIds[0] === filterIds) {
    filterStore.value.boardingIds = []
  }

  filterStore.value.amenityIds = filterStore.value.amenityIds.filter((id) => filterIds !== id)

  if (filterStore.value.themeId === filterIds) {
    filterStore.value.themeId = 0
  }
}

function removeFullFilter(): void {
  filterStore.value = {
    areaId: filterStore.value.areaId,
    startDate: null,
    endDate: null,
    rooms: null,
    accotypeIds: [],
    themeId: 0,
    amenityIds: [],
    boardingIds: [],
    ratingFrom: 0,
    ratingTo: 5.5,
    companyIds: []
  }
  ratingsFilterSelected.value = []
}

onMounted(() => {
  addAppliedFilters(filterStore.value)
})

watch(
  () => filterStore,
  (filter) => {
    addAppliedFilters(filter.value)
  },
  { deep: true }
)

watch(
  () => ratingsFilterSelected,
  () => {
    emit('ratings-selected', ratingsFilterSelected.value)
  },
  { deep: true }
)
</script>

<template>
  <div class="my-2 flex gap-2 overflow-x-scroll pb-2 sm:flex-wrap sm:overflow-hidden sm:border-0 sm:pb-0" v-if="appliedFilters.appliedFilter.length > 0">
    <div v-for="filter in appliedFilters.appliedFilter" :key="filter.id.toString()" class="pui-appliedfilter flex">
      <button @click="removeAppliedFilters(filter.id)" class="flex">
        <span class="mr-1">{{ filter.name }}</span>
        <CloseSvg />
      </button>
    </div>
    <button @click="removeFullFilter()" class="text-nowrap text-[70%] font-semibold" v-if="appliedFilters.appliedFilter.length > 1">
      <span class="mr-1">{{ t(`${CULTURE}.filter.appliedFilters.removeAll`) }}</span>
    </button>
  </div>
</template>
